import React, { useCallback, useEffect, useMemo, useState } from "react";
import EntityForm from "../EntityForm/EntityForm";
import { entityToFormValuesMapper } from "../../Common/utils";
import CONSTANTS from "../constants";
import "./customStyles.css";
import { usePropertyConfiguration } from "../../../../components/property-configuration-provider";
import { FullWidthLoader } from "../../../../components/full-width-loader";
import { ContainerLoader } from "../Promotions/PromotionsForm";

function isCartAbandonmentType(type) {
  return type === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.CART_ABANDONMENT;
}

function hasGuestTypeDefined(formValues) {
  return !!formValues.emailGuestType;
}

function customEntityToFormValuesMapper({ entity, fields, locales, translationConfig }) {
  const formValues = entityToFormValuesMapper({
    entity,
    fields,
    locales,
    translationConfig,
  });

  formValues.templateType = CONSTANTS.ENTITY.EMAIL_TEMPLATE.TEMPLATE_TYPES.DEFAULT;

  if (
    entity.id &&
    !formValues.translations.find(t => t.header) ||
    !formValues.translations.find(t => t.footer)
  ) {
    formValues.templateType = CONSTANTS.ENTITY.EMAIL_TEMPLATE.TEMPLATE_TYPES.CUSTOM;
  }

  const delayMultiplier =
    formValues.type === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.PRE_STAY ||
    formValues.type === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.POST_STAY
      ? 1 / (24 * 60)
      : 1;
  formValues.delayCheckout = Number(formValues.delay) * delayMultiplier;
  formValues.delayCheckin = Number(formValues.delay) * delayMultiplier;
  if (isCartAbandonmentType(formValues.type)) {
    formValues.delayCartAbandonment =
      formValues.delay ||
      formValues.delayCartAbandonment ||
      CONSTANTS.ENTITY.CART_ABANDONMENT.defaultDelay;
  }
  return {
    ...formValues,
    isActive: entity.active,
  };
}

const isUseStandardTemplate = ({ formValues }) => {
  return formValues.templateType === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TEMPLATE_TYPES.DEFAULT;
};

const getChronosUnit = formValues => {
  switch (formValues.type) {
    case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.POST_STAY:
    case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.PRE_STAY:
    case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.CART_ABANDONMENT:
      return CONSTANTS.ENTITY.EMAIL_TEMPLATE.CHRONO_UNITS.MINUTES;
    default:
      return formValues.chronoUnit;
  }
};

const getDelay = formValues => {
  switch (formValues.type) {
    case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.POST_STAY:
      return Number(formValues.delayCheckout) * 24 * 60;
    case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.PRE_STAY:
      return Number(formValues.delayCheckin) * 24 * 60;
    case CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.CART_ABANDONMENT:
      return Number(formValues.delayCartAbandonment);
    default:
      return formValues.delay;
  }
};

const isReservationConfirmationType = type => {
  return type === CONSTANTS.ENTITY.EMAIL_TEMPLATE.TYPES.RESERVATION_CONFIRMATION;
};

const getValidSingleMailOnMultipleRoomStaysOption = formValues => {
  return isReservationConfirmationType(formValues.type)
    ? formValues.singleMailOnMultipleRoomStays
    : false;
};

export function EmailTemplateForm({
  locales,
  entity,
  entityError,
  entityResult,
  loading,
  getListPath,
  config,
  onSubmitEntity,
}) {
  const { name, validation, fields: fieldsConfig, renderers, translation, entities } = config;
  const propertyConfig = usePropertyConfiguration();
  const [isSubmit, setIsSubmit] = useState(false);
  const [fields, setFields] = useState({});
  const [isLoading, setIsLoading] = useState(true);

  const showAdvancedEmailsManager = useMemo(
    () => propertyConfig && propertyConfig.showAdvancedEmailsManager === "1",
    [propertyConfig]
  );

  useEffect(() => {
    if (propertyConfig) {
      if (!showAdvancedEmailsManager) {
        const fieldType = fieldsConfig.form.find(fieldForm => fieldForm.fieldName === "type");
        const newOptions = fieldType && fieldType.optionDisplay;
        delete newOptions["POST_STAY"];
        delete newOptions["PRE_STAY"];
        fieldsConfig.form.find(
          fieldForm => fieldForm.fieldName === "type" && (fieldForm.optionDisplay = newOptions)
        );
        fieldsConfig.form = fieldsConfig.form.filter(
          fieldForm => fieldForm.fieldName !== "useStandardTemplate"
        );
      }
      setFields(fieldsConfig);
      if (isLoading) {
        setIsLoading(false);
      }
    }
  }, [config, fieldsConfig, isLoading, propertyConfig, showAdvancedEmailsManager]);

  const formFields = useMemo(() => fields && fields.form, [fields]);

  const handleSubmit = useCallback(
    async ({ formValues }) => {
      setIsSubmit(true);
      //TODO: handle case when there's an original entity
      //TODO: this could be handled in a generic way.
      const entity = {
        hotelTicker: formValues.hotelTicker,
        type: formValues.type,
        delay: getDelay(formValues),
        chronoUnit: getChronosUnit(formValues),
        emailDestinationType: formValues.emailDestinationType,
        isActive: formValues.isActive,
        singleMailOnMultipleRoomStays: getValidSingleMailOnMultipleRoomStaysOption(formValues),
      };
      if (
        config.hasOwnProperty("translation") &&
        config.translation &&
        config.translation.hasOwnProperty("fieldName")
      ) {
        const translationField = config.translation.fieldName;
        const translationValues =
          (formValues.hasOwnProperty(translationField) && formValues[translationField]) || [];
        entity[config.translation.fieldName] = translationValues.map(translation => {
          if (isUseStandardTemplate({ formFields, formValues })) {
            return {
              locale: translation.locale,
              header: translation.header,
              footer: translation.footer,
            };
          }
          return {
            locale: translation.locale,
            subject: translation.subject,
            body: translation.body,
          };
        });
      }
      if (isCartAbandonmentType(formValues.type) && hasGuestTypeDefined(formValues)) {
        entity.emailGuestType = formValues.emailGuestType;
      }
      if (formValues.id) {
        entity.id = formValues.id;
      }
      try {
        onSubmitEntity({
          entity,
        });
      } catch (error) {}
      setIsSubmit(false);
    },
    [config, onSubmitEntity, formFields]
  );

  if (isLoading) {
    return (
      <ContainerLoader>
        <FullWidthLoader />
      </ContainerLoader>
    );
  }

  return (
    <EntityForm
      loading={loading || isSubmit}
      name={name}
      entity={entity}
      entities={entities}
      validation={validation}
      renderers={renderers}
      fields={formFields}
      locales={locales}
      translation={translation}
      onSubmit={handleSubmit}
      entityError={entityError}
      entityResult={entityResult}
      getListPath={getListPath}
      entityToFormValuesMapper={customEntityToFormValuesMapper}
    />
  );
}

EmailTemplateForm.propTypes = {};

EmailTemplateForm.defaultProps = {
  locales: ["es", "ca", "en", "fr", "it", "de"],
};

export default EmailTemplateForm;
